import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Eye, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/store/cartStore";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getJewelryGradient } from "@/lib/colorGradient";
import { supabase } from "@/integrations/supabase/client";

interface Variant {
  id: string;
  title: string;
  price: string;
  currency: string;
  availableForSale: boolean;
  selectedOptions?: Array<{ name: string; value: string }>;
  image?: string | null;
}

interface ProductOption {
  name: string;
  values: string[];
}

interface Product {
  id: string;
  title: string;
  handle: string;
  price: string;
  currency: string;
  image: string | null;
  variantId: string;
  availableForSale: boolean;
  variants?: Variant[];
  options?: ProductOption[];
  selectedVariantIndex?: number;
}

interface ProductResultCardProps {
  product: Product;
  index: number;
}

// Check if option is a color option
const isColorOptionName = (name: string): boolean => {
  const n = name.toLowerCase();
  return (
    n === 'color' ||
    n === 'colour' ||
    n.includes('צבע') ||
    n.includes('color') ||
    n.includes('colour') ||
    n === 'גוון' ||
    n === 'shade' ||
    n === 'metal' ||
    n === 'מתכת'
  );
};

export function ProductResultCard({ product, index }: ProductResultCardProps) {
  const addItem = useCartStore((state) => state.addItem);
  const setCartOpen = useCartStore((state) => state.setCartOpen);
  const [isHovered, setIsHovered] = useState(false);
  const [colorHexValues, setColorHexValues] = useState<Record<string, string>>({});
  const [isLoadingColors, setIsLoadingColors] = useState(false);
  
  // Get variants or use default
  const variants = product.variants || [{
    id: product.variantId,
    title: "Default",
    price: product.price,
    currency: product.currency,
    availableForSale: product.availableForSale,
  }];
  
  // Find color option
  const colorOption = useMemo(() => {
    return product.options?.find(opt => isColorOptionName(opt.name));
  }, [product.options]);

  // Get variant by color value
  const getVariantByColor = (colorValue: string): Variant | undefined => {
    return variants.find((v) =>
      v.selectedOptions?.some((opt) => isColorOptionName(opt.name) && opt.value === colorValue)
    );
  };

  // Initialize with pre-selected variant from search or first color option
  const getInitialColorValue = (): string | undefined => {
    // If there's a pre-selected variant index from search
    if (product.selectedVariantIndex !== undefined && product.selectedVariantIndex > 0) {
      const preSelectedVariant = variants[product.selectedVariantIndex];
      if (preSelectedVariant?.selectedOptions) {
        const colorOpt = preSelectedVariant.selectedOptions.find(opt => isColorOptionName(opt.name));
        if (colorOpt) return colorOpt.value;
      }
    }
    // Default to first color option
    return colorOption?.values?.[0];
  };

  const [selectedColorValue, setSelectedColorValue] = useState<string | undefined>(
    getInitialColorValue()
  );
  
  const selectedVariant = selectedColorValue 
    ? getVariantByColor(selectedColorValue) || variants[product.selectedVariantIndex || 0] || variants[0]
    : variants[product.selectedVariantIndex || 0] || variants[0];

  // Get current image based on selected variant
  const currentImage = selectedVariant?.image || product.image;

  // Fetch color hex values
  useEffect(() => {
    const fetchColors = async () => {
      if (!colorOption || colorOption.values.length === 0) return;
      
      setIsLoadingColors(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-colors-batch', {
          body: { colorNames: colorOption.values }
        });
        
        if (!error && data?.colors) {
          setColorHexValues(data.colors);
        }
      } catch (err) {
        console.error('Error fetching colors:', err);
      } finally {
        setIsLoadingColors(false);
      }
    };

    fetchColors();
  }, [colorOption]);

  const handleColorSelect = (e: React.MouseEvent, colorValue: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedColorValue(colorValue);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedVariant?.availableForSale) {
      toast.error("מוצר זה אינו זמין כרגע");
      return;
    }

    // Build a minimal ShopifyProduct structure for the cart
    const shopifyProduct = {
      id: product.id,
      title: product.title,
      description: "",
      handle: product.handle,
      priceRange: {
        minVariantPrice: {
          amount: selectedVariant.price,
          currencyCode: selectedVariant.currency || "ILS",
        },
      },
      images: {
        edges: currentImage
          ? [{ node: { url: currentImage, altText: product.title } }]
          : [],
      },
      variants: {
        edges: variants.map(v => ({
          node: {
            id: v.id,
            title: v.title,
            price: {
              amount: v.price,
              currencyCode: v.currency || "ILS",
            },
            availableForSale: v.availableForSale,
            selectedOptions: v.selectedOptions || [],
          },
        })),
      },
      options: product.options || [],
    };

    addItem({
      product: shopifyProduct as any,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: {
        amount: selectedVariant.price,
        currencyCode: selectedVariant.currency || "ILS",
      },
      quantity: 1,
      variantImage: currentImage
        ? { url: currentImage, altText: product.title }
        : undefined,
    });

    toast.success("נוסף לסל!", {
      description: `${product.title}${selectedColorValue ? ` - ${selectedColorValue}` : ''}`,
    });

    setCartOpen(true);
  };

  const formattedPrice = new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: selectedVariant?.currency || product.currency || "ILS",
    minimumFractionDigits: 0,
  }).format(parseFloat(selectedVariant?.price || product.price));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.1 * index }}
      className="bg-background rounded-lg border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      <Link 
        to={`/products/${product.handle}${selectedColorValue ? `?color=${encodeURIComponent(selectedColorValue)}` : ''}`} 
        className="block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="aspect-square relative overflow-hidden bg-muted">
          {currentImage ? (
            <img
              src={currentImage}
              alt={product.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ExternalLink className="h-8 w-8" />
            </div>
          )}
          
          {/* Hover overlay with preview button */}
          <motion.div
            initial={false}
            animate={{ opacity: isHovered ? 1 : 0 }}
            className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
              <Eye className="h-5 w-5 text-foreground" />
            </div>
          </motion.div>
        </div>
      </Link>

      <div className="p-2 space-y-1.5">
        <Link to={`/products/${product.handle}${selectedColorValue ? `?color=${encodeURIComponent(selectedColorValue)}` : ''}`}>
          <h4 className="text-xs font-medium line-clamp-2 hover:text-primary transition-colors leading-tight">
            {product.title}
          </h4>
        </Link>

        <p className="text-sm font-bold text-primary">{formattedPrice}</p>

        {/* Color Swatches */}
        {colorOption && colorOption.values.length > 1 && (
          <TooltipProvider delayDuration={100}>
            <div className="flex items-center gap-1 flex-wrap py-1">
              {colorOption.values.slice(0, 4).map((colorValue) => {
                const hexColor = colorHexValues[colorValue];
                const isWhiteColor = hexColor?.toUpperCase() === '#FFFFFF' || 
                                    hexColor?.toUpperCase() === '#E8E8E8' ||
                                    hexColor?.toUpperCase() === '#E5E4E2';
                const isSelected = selectedColorValue === colorValue;
                
                return (
                  <Tooltip key={colorValue}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => handleColorSelect(e, colorValue)}
                        className={`w-4 h-4 rounded-full transition-all duration-200 hover:scale-110 relative overflow-hidden ${
                          isWhiteColor ? 'border border-border' : ''
                        } ${
                          isSelected ? 'ring-2 ring-primary ring-offset-1 scale-110' : ''
                        }`}
                        style={{ 
                          background: isLoadingColors && !hexColor 
                            ? '#e5e5e5' 
                            : hexColor 
                              ? getJewelryGradient(hexColor) 
                              : getJewelryGradient('#D4AF37'),
                        }}
                        aria-label={colorValue}
                      >
                        {isLoadingColors && !hexColor && (
                          <span className="block w-full h-full rounded-full animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent 
                      side="top" 
                      className="bg-foreground text-background text-xs px-2 py-1 z-[100]"
                    >
                      {colorValue}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
              {colorOption.values.length > 4 && (
                <span className="text-[10px] text-muted-foreground">+{colorOption.values.length - 4}</span>
              )}
            </div>
          </TooltipProvider>
        )}

        <Button
          size="sm"
          variant="default"
          className="w-full h-7 text-xs"
          onClick={handleAddToCart}
          disabled={!selectedVariant?.availableForSale}
        >
          <ShoppingBag className="h-3 w-3 ml-1" />
          {selectedVariant?.availableForSale ? "הוסף לסל" : "אזל"}
        </Button>
      </div>
    </motion.div>
  );
}
